import React, { useCallback, useState } from 'react';
import { MILESTONE_DEFS } from '../gags/GagGanttView';
import { MilestoneShape, MILESTONE_SHAPE_CLASSES } from '../teams/CrossTeamGanttView';

/**
 * THE gag-milestone legend of a Gantt, and the switch behind it (owner
 * 2026-10-13, on the Breakdown Timeline, then "use these changes as our
 * defaults for all gantt charts where common features exist"):
 *
 *   - the four markers, in the order Prep, Access, Shoot, Strike, each in the
 *     shape and hue it is drawn in on the chart;
 *   - each entry is a TOGGLE: a click hides that kind of marker and greys the
 *     entry; another click brings it back;
 *   - by default ONLY the shoot date shows; Labour starts with all four on
 *     (owner 2026-10-13: "default is on") by passing `NO_HIDDEN_MILESTONES`.
 *
 * `useMilestoneVisibility(initiallyHidden?)` → { hidden, isHidden(field), toggle(field) }.
 * Session state on purpose: no chart here stores a view choice.
 *
 * It governs markers drawn ON A SUMMARY (a collapsed gag's bar, a Labour owner
 * rail). A surface that exists to show ONE gag's milestones (an expanded gag's
 * Milestones lane, the gag panel's own chart) shows all four and ignores it.
 *
 * "Off" is carried by `aria-pressed`, the strike-through and the `title`, never
 * by the grey alone. Display only: it decides nothing and writes nothing.
 */
export const LEGEND_MILESTONE_ORDER = ['prep_date', 'location_access_date', 'shoot_date', 'strike_date'];
export const LEGEND_MILESTONE_DEFS = LEGEND_MILESTONE_ORDER.map((field) => MILESTONE_DEFS.find((def) => def.field === field));
export const DEFAULT_HIDDEN_MILESTONES = ['prep_date', 'location_access_date', 'strike_date'];
export const NO_HIDDEN_MILESTONES = [];

export function useMilestoneVisibility(initiallyHidden = DEFAULT_HIDDEN_MILESTONES) {
  const [hidden, setHidden] = useState(() => new Set(initiallyHidden));
  const toggle = useCallback((field) => setHidden((prev) => {
    const next = new Set(prev);
    if (next.has(field)) next.delete(field); else next.add(field);
    return next;
  }), []);
  const isHidden = useCallback((field) => hidden.has(field), [hidden]);
  return { hidden, isHidden, toggle };
}

export default function GanttMilestoneLegend({ isHidden, onToggle, testIdPrefix = 'gantt-legend' }) {
  return (
    <>
      {LEGEND_MILESTONE_DEFS.map((def) => {
        const off = isHidden(def.field);
        return (
          <button
            key={def.field} type="button"
            data-testid={`${testIdPrefix}-${def.field}`}
            aria-pressed={!off}
            onClick={() => onToggle(def.field)}
            title={off ? `Show ${def.label} markers` : `Hide ${def.label} markers`}
            className={`flex items-center gap-1 rounded px-1 -mx-1 py-0.5 text-xs transition-colors hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${off ? 'text-gray-300 dark:text-gray-600' : (MILESTONE_SHAPE_CLASSES[def.shape] || MILESTONE_SHAPE_CLASSES.diamond)}`}
          >
            <MilestoneShape shape={def.shape} />
            <span className={off ? 'text-gray-400 dark:text-gray-500 line-through' : 'text-gray-500 dark:text-gray-400'}>{def.label}</span>
          </button>
        );
      })}
    </>
  );
}
